// Service for handling API calls to AI services

import { i18n } from "@lingui/core";
import { msg } from "@lingui/macro";
import { createId } from "@paralleldrive/cuid2";

import { toast } from "../../hooks/use-toast";
import logger from "../../utils/logger";
import { agentsService } from "../agents.service";
import { PerplexityRateLimiter } from "./perplexity-rate-limiter";
import { PromptBuilder } from "./promptBuilderService";

const AGENT_API_KEY = import.meta.env.VITE_AGENT_API_KEY;
const AGENT_URL = import.meta.env.VITE_AGENT_URL;
const ONET_API_USERNAME = import.meta.env.VITE_ONET_API_USERNAME;
const ONET_API_PASSWORD = import.meta.env.VITE_ONET_API_PASSWORD;
const FORMATTER_API_KEY = import.meta.env.VITE_FORMATTER_API_KEY;
const FORMATTER_URL = import.meta.env.VITE_FORMATTER_URL;

export type ResumeGenerationResult = {
  content: string;
  tokenUsage?: number | null;
};

export type ATSAnalyticsResult = {
  keywordAnalysis: {
    totalKeywords: number;
    keywordDensity: number;
    hardSkillsCount: number;
    hardSkillsPercentage: number;
    softSkillsCount: number;
    softSkillsPercentage: number;
    industryTermsCount: number;
    industryTermsPercentage: number;
    actionVerbsCount: number;
    actionVerbsPercentage: number;
    readabilityScore: number;
    readabilityLevel: string;
    keywordFrequency: Record<string, number>;
    keywordImportance: Record<string, string>;
    missingKeywords: string[];
    hardSkillsCoverage: number;
    softSkillsBalance: number;
    keywordDistribution: {
      summary: number;
      experience: number;
      skills: number;
      education: number;
    };
  };
  sectionStrengthData: Record<string, number>;
  suggestions: {
    highPriority: {
      title: string;
      description: string;
      suggestion: string;
    }[];
    mediumPriority: {
      title: string;
      description: string;
      suggestion: string;
    }[];
    lowPriority: {
      title: string;
      description: string;
      suggestion: string;
    }[];
  };
  tokenUsage?: number | null;
};

// Helper function to get required structure (moved inside function to prevent hash keys)
const getRequiredStructure = () => ({
  basics: {
    url: { href: "", label: "" },
    name: "Your Name",
    phone: "(123) 456-7890",
    // Note: email field is intentionally omitted - will be handled by user profile
    picture: {
      url: "/artboard/images/default-profile.jpg",
      size: 277,
      effects: { border: false, hidden: false, grayscale: false },
      aspectRatio: 1,
      borderRadius: 1,
    },
    headline: "",
    idNumber: "",
    location: "New York, US",
    dateOfBirth: "",
    nationality: "",
    customFields: [],
    maritalStatus: "",
  },
  metadata: {
    schemaVersion: "1.0.0",
    template: "vibrant",
    layout: [
      [
        ["summary", "experience", "education", "projects", "certifications", "awards", "mostProudOf", "myMind"],
        [
          "skills",
          "languages",
          "profiles",
          "interests",
          "hobbies",
          "volunteer",
          "publications",
          "references",
          "signature",
        ],
      ],
    ],
    css: {
      value: "",
      visible: false,
    },
    page: {
      margin: 42,
      format: "a4",
      options: {
        breakLine: false,
        pageNumbers: true,
      },
    },
    theme: {
      background: "#ffffff",
      text: "#000000",
      primary: "#0284c7",
      isFree: "NO",
    },
    typography: {
      font: {
        family: "Playfair Display",
        subset: "latin",
        variants: ["regular"],
        size: 16.5,
      },
      bodyFont: {
        family: "Miriam Libre",
        subset: "latin",
        variants: ["regular"],
      },
      lineHeight: 1.75,
      hideIcons: true,
      underlineLinks: true,
      headerFontSize: 24,
    },
    notes: "",
    backgroundImage: "bg7",
    signature: "",
    locale: "en-US", // Will be dynamically set by enforceSchema function
  },
  sections: {
    summary: {
      id: "summary",
      name: "Summary",
      columns: 1,
      separateLinks: true,
      visible: true,
      content: "",
    },
    experience: {
      id: "experience",
      name: "Experience",
      columns: 1,
      separateLinks: true,
      visible: true,
      items: [],
    },
    education: {
      id: "education",
      name: "Education",
      columns: 1,
      separateLinks: true,
      visible: true,
      items: [],
    },
    skills: {
      id: "skills",
      name: "Skills",
      columns: 1,
      separateLinks: true,
      visible: true,
      items: [],
    },
    languages: {
      id: "languages",
      name: "Languages",
      columns: 1,
      separateLinks: true,
      visible: false,
      items: [],
    },
    certifications: {
      id: "certifications",
      name: "Certifications",
      columns: 1,
      separateLinks: true,
      visible: false,
      items: [],
    },
    projects: {
      id: "projects",
      name: "Projects",
      columns: 1,
      separateLinks: true,
      visible: false,
      items: [],
    },
    awards: {
      id: "awards",
      name: "Awards",
      columns: 1,
      separateLinks: true,
      visible: false,
      items: [],
    },
    volunteer: {
      id: "volunteer",
      name: "Volunteering",
      columns: 1,
      separateLinks: true,
      visible: false,
      items: [],
    },
    interests: {
      id: "interests",
      name: "Interests",
      columns: 1,
      separateLinks: true,
      visible: false,
      items: [],
    },
    publications: {
      id: "publications",
      name: "Publications",
      columns: 1,
      separateLinks: true,
      visible: false,
      items: [],
    },
    profiles: {
      id: "profiles",
      name: "Profiles",
      columns: 1,
      separateLinks: true,
      visible: false,
      items: [],
    },
    references: {
      id: "references",
      name: "References",
      columns: 1,
      separateLinks: true,
      visible: false,
      items: [],
    },
    hobbies: {
      id: "hobbies",
      name: "Hobbies",
      columns: 1,
      separateLinks: true,
      visible: false,
      items: [],
    },
    signature: {
      id: "signature",
      name: "Signature",
      columns: 1,
      separateLinks: true,
      visible: false,
      items: [],
    },
    custom: {},
  },
});

function cleanAIJson(raw: string): string {
  let json = raw.trim();
  if (json.startsWith("```")) {
    json = json.replace(/^```json|^```|```$/g, "").trim();
  }
  return json;
}

// Root keys allowed by our app schema
const ALLOWED_ROOT_KEYS = new Set(["basics", "metadata", "sections"]);
// Known section keys from the required structure template
const KNOWN_SECTION_KEYS = new Set(Object.keys(getRequiredStructure().sections));

// Normalize AI root keys to match our schema contract.
// - Keep only basics, metadata, sections at root
// - Move known section-like roots into sections
// - Map scores to metadata.scores
// - Put any other extras into metadata.custom
// - Drop DB-level fields (title/slug/visibility) from resume JSON
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeRootKeys(parsed: any): any {
  // Ensure objects
  const normalized: any = {};
  const incoming = parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};

  // Start with provided roots when present
  if (incoming.basics && typeof incoming.basics === "object") {
    normalized.basics = incoming.basics;
  }

  normalized.metadata =
    incoming.metadata && typeof incoming.metadata === "object" ? { ...incoming.metadata } : {};
  if (!normalized.metadata.custom || typeof normalized.metadata.custom !== "object") {
    normalized.metadata.custom = {};
  }

  normalized.sections =
    incoming.sections && typeof incoming.sections === "object" ? { ...incoming.sections } : {};

  // Helper to move a key into sections if it looks like a section
  const moveToSections = (key: string, value: any) => {
    try {
      if (!normalized.sections || typeof normalized.sections !== "object") {
        normalized.sections = {};
      }
      // If value is a plain string (e.g. summary text), wrap it as a section object
      if (typeof value === "string") {
        normalized.sections[key] = {
          id: key,
          name: key.charAt(0).toUpperCase() + key.slice(1),
          content: value.startsWith("<") ? value : `<p>${value}</p>`,
          columns: 1,
          visible: true,
          items: [],
        };
      } else {
        normalized.sections[key] = value;
      }
    } catch {
      // fallback to metadata.custom if anything goes wrong
      normalized.metadata.custom[key] = value;
    }
  };

  // Collect and remap extra root-level keys
  for (const key of Object.keys(incoming)) {
    if (ALLOWED_ROOT_KEYS.has(key)) continue;
    const value = incoming[key];

    // Known DB-level fields are not part of resume JSON
    if (key === "title" || key === "slug" || key === "visibility") {
      continue;
    }

    // Scores mapping
    if (key === "atsScore" || key === "jobFitScore") {
      if (!normalized.metadata.scores || typeof normalized.metadata.scores !== "object") {
        normalized.metadata.scores = {};
      }
      normalized.metadata.scores[key] = value;
      continue;
    }

    // If the key is a known section name, or the value looks like a section
    const looksLikeSection =
      KNOWN_SECTION_KEYS.has(key) ||
      (value && typeof value === "object" && ("items" in value || "content" in value));

    if (looksLikeSection) {
      moveToSections(key, value);
      continue;
    }

    // Any other extra data goes to metadata.custom
    normalized.metadata.custom[key] = value;
  }

  return normalized;
}

export function enforceSchema(
  aiOutput: string | Record<string, unknown>,
  language?: string,
): Record<string, unknown> {
  logger.log("🔧 enforceSchema called - processing AI output");
  let parsed;

  // Create dynamic required structure with language-specific locale and metadata
  const getDynamicRequiredStructure = (lang?: string) => {
    const localeMap: Record<string, string> = {
      AR: "ar-SA",
      FR: "fr-FR",
      ES: "es-ES",
      DE: "de-DE",
      IT: "it-IT",
      PT: "pt-PT",
      NL: "nl-NL",
      RU: "ru-RU",
      ZH: "zh-CN",
      JA: "ja-JP",
      KO: "ko-KR",
    };

    const dynamicStructure = { ...getRequiredStructure() };
    const targetLocale = lang && localeMap[lang] ? localeMap[lang] : "en-US";
    dynamicStructure.metadata.locale = targetLocale;

    // Arabic-specific metadata customization
    if (lang === "AR") {
      console.log("🇸🇦 Applying Arabic-specific metadata template");
      dynamicStructure.metadata = {
        schemaVersion: "1.0.0",
        css: {
          value: "",
          visible: false,
        },
        page: {
          format: "a4",
          margin: 42,
          options: {
            breakLine: false,
            pageNumbers: true,
          },
        },
        notes: "",
        theme: {
          text: "#000000",
          isFree: "NO",
          primary: "#1b79db",
          background: "#ffffff",
        },
        layout: [
          [
            ["summary", "experience", "education", "projects", "certifications", "awards", "mostProudOf", "myMind"],
            [
              "skills",
              "languages",
              "profiles",
              "interests",
              "hobbies",
              "volunteer",
              "publications",
              "references",
              "signature",
            ],
          ],
        ],
        locale: "ar-SA",
        template: "vibrant",
        signature: "Your Name",
        typography: {
          font: {
            size: 16,
            family: "Tajawal",
            subset: "latin",
            variants: ["regular"],
          },
          bodyFont: {
            family: "Tajawal",
            subset: "arabic",
            variants: ["regular"],
          },
          hideIcons: true,
          lineHeight: 1.75,
          headerFontSize: 23,
          underlineLinks: true,
        },
        backgroundImage: "2",
      };
    }

    console.log("🔄 Language mapping:", lang, "->", targetLocale);
    console.log("📋 Dynamic structure locale:", dynamicStructure.metadata.locale);

    return dynamicStructure;
  };

  const requiredStructure = getDynamicRequiredStructure(language);

  try {
    // If already an object, use it directly
    if (typeof aiOutput === "object") {
      parsed = aiOutput;
    } else {
      // Clean and parse string input
      const jsonString = aiOutput;

      // Try normal parsing first
      try {
        parsed = JSON.parse(cleanAIJson(jsonString));
      } catch {
        // Use aggressive cleanup if normal parsing fails
        logger.log("Normal JSON parsing failed, trying aggressive cleanup...");
        const cleanedJson = AiApiService.aggressiveJSONCleanup(jsonString);

        try {
          parsed = JSON.parse(cleanedJson);
        } catch {
          // Try extracting partial JSON as last resort
          logger.log("Aggressive cleanup failed, extracting partial JSON...");
          const extracted = AiApiService.extractPartialJSON(jsonString);

          if (Object.keys(extracted).length > 0) {
            parsed = extracted;
          } else {
            // Throw with more helpful error message
            throw new Error(`Invalid JSON from AI. Raw response: ${jsonString.slice(0, 500)}...`);
          }
        }
      }
    }
  } catch (error) {
    console.error("JSON parsing error:", error);
    throw new Error("Invalid JSON from AI");
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function preserveAiData(aiData: any, template: any): any {
    // If template is an array, ALWAYS preserve AI array if it exists
    if (Array.isArray(template)) {
      // If AI has an array, use it regardless of template
      if (Array.isArray(aiData)) {
        logger.log("🔧 Preserving AI array data with", aiData.length, "items");
        return aiData;
      }
      // Only use template array if AI data is missing
      return template;
    }

    // If template is an object, merge AI data with template structure
    if (typeof template === "object" && template !== null) {
      // Start with AI data if it exists and is an object
      const result: any =
        aiData && typeof aiData === "object" && !Array.isArray(aiData) ? { ...aiData } : {};

      // Only add missing keys from template (don't overwrite AI data)
      for (const key in template) {
        if (Object.prototype.hasOwnProperty.call(template, key)) {
          if (key in result) {
            // Key exists in AI data, recursively preserve AI structure but ensure template requirements
            // IMPORTANT: For 'items' key specifically, always preserve AI data
            if (key === "items" && Array.isArray(result[key])) {
              logger.log(`🔧 Preserving AI items for section, count: ${result[key].length}`);
              // Don't recurse into items, keep them as-is
              continue;
            }

            // Special handling for locale - ALWAYS prefer template locale when we have a specific language
            if (key === "locale" && language) {
              console.log(
                "🔧 AI provided locale:",
                result[key],
                "but FORCING template locale:",
                template[key],
              );
              result[key] = template[key]; // FORCE template locale when we have a specific language
            } else {
              result[key] = preserveAiData(result[key], template[key]);
            }
          } else {
            // Key doesn't exist in AI data, use template default
            result[key] =
              typeof template[key] === "object"
                ? preserveAiData(undefined, template[key])
                : template[key];

            // Special logging for locale
            if (key === "locale") {
            }
          }
        }
      }

      return result;
    }

    // For primitive values, prefer AI data over template
    return aiData === undefined ? template : aiData;
  }

  // Normalize root keys before merging with required structure
  parsed = normalizeRootKeys(parsed);

  logger.log("🔧 Before preserveAiData - normalized AI data:", JSON.stringify(parsed, null, 2));
  logger.log("🔧 Required structure locale:", requiredStructure.metadata.locale);

  const result = preserveAiData(parsed, requiredStructure);

  logger.log("🔧 After preserveAiData - merged result:", JSON.stringify(result, null, 2));
  // Generate proper CUID2 IDs for all section items and fix signature
  const processedResult = generateProperIds(result);
  logger.log("✅ enforceSchema completed - URLs should be fixed");
  return processedResult;
}

/**
 * Generates proper CUID2 IDs for all section items and ensures all required fields have proper default values
 */
function generateProperIds(resumeData: any): any {
  const result = { ...resumeData };

  // Fix metadata signature field (should be string, not object)
  if (result.metadata?.signature && typeof result.metadata.signature === "object") {
    result.metadata.signature = "";
  }

  // Helper function to validate and fix URLs - Ultra defensive version
  const fixUrl = (url: any) => {
    // Default to empty, valid structure
    const defaultResult = { label: "", href: "" };

    try {
      if (!url || typeof url !== "object") {
        return defaultResult;
      }

      // Ensure label is a string
      const label = typeof url.label === "string" ? url.label : "";

      // Start with empty href (always valid)
      let href = "";

      if (url.href && typeof url.href === "string") {
        const urlString = url.href.trim();

        // Empty string is always valid
        if (urlString === "") {
          return { label, href: "" };
        }

        // Ultra-defensive URL processing
        try {
          let candidateUrl = urlString;

          // Remove any dangerous characters and clean up
          candidateUrl = candidateUrl.replace(/["'<>]/g, "");
          candidateUrl = candidateUrl.replace(/\s+/g, "");

          // Must have a domain pattern with at least one dot
          if (!candidateUrl.includes(".")) {
            return { label, href: "" };
          }

          // Add protocol if missing but only for domain-like strings
          if (!/^https?:\/\//i.test(candidateUrl)) {
            // Very strict domain validation
            if (
              /^[\dA-Za-z][\d.A-Za-z-]*[\dA-Za-z]\.[A-Za-z]{2,}(:\d+)?(\/\S*)?$/.test(candidateUrl)
            ) {
              candidateUrl = "https://" + candidateUrl;
            } else {
              return { label, href: "" };
            }
          }

          // Test with URL constructor
          const testUrl = new URL(candidateUrl);

          // Only allow http/https
          if (testUrl.protocol !== "http:" && testUrl.protocol !== "https:") {
            return { label, href: "" };
          }

          // Must have valid hostname
          if (!testUrl.hostname || testUrl.hostname.length < 3 || !testUrl.hostname.includes(".")) {
            return { label, href: "" };
          }

          // Additional security checks
          if (
            testUrl.hostname === "localhost" ||
            testUrl.hostname.startsWith("127.") ||
            testUrl.hostname.startsWith("192.168.") ||
            testUrl.hostname.startsWith("10.")
          ) {
            // Allow localhost and private IPs for development
            // But be extra careful about the format
          }

          // Final cleanup
          let finalUrl = testUrl.href;

          // Remove trailing slashes
          if (finalUrl.endsWith("/") && finalUrl !== "https://" && finalUrl !== "http://") {
            finalUrl = finalUrl.slice(0, -1);
          }

          // Final validation with strict regex
          href = /^https?:\/\/[\dA-Za-z][\d.A-Za-z-]*[\dA-Za-z]+(:\d+)?(\/\S*)?$/.test(finalUrl)
            ? finalUrl
            : "";
        } catch {
          // Any error at all results in empty string
          href = "";
        }
      }

      return { label, href };
    } catch {
      // Any error in the entire function returns the safe default
      return defaultResult;
    }
  };

  // Generate proper CUID2 IDs and fix required fields for all section items
  if (result.sections) {
    for (const sectionKey of Object.keys(result.sections)) {
      const section = result.sections[sectionKey];
      if (section?.items && Array.isArray(section.items)) {
        const seenIds = new Set<string>();
        section.items.forEach((item: any) => {
          // Fix ID if invalid OR duplicated within the section
          if (
            item &&
            (!item.id ||
              typeof item.id !== "string" ||
              !/^[\da-z]+$/.test(item.id) ||
              seenIds.has(item.id))
          ) {
            item.id = createId();
          }
          if (item && typeof item.id === "string") {
            seenIds.add(item.id);
          }

          // Ensure required fields have proper default values based on section type
          if (item) {
            // Common required fields for all items
            if (item.visible === undefined) item.visible = true;

            // Section-specific required fields
            switch (sectionKey) {
              case "publications": {
                if (!item.name) item.name = "";
                if (!item.publisher) item.publisher = "";
                if (!item.date) item.date = "";
                if (!item.summary) item.summary = "";
                item.url = fixUrl(item.url);
                break;
              }

              case "experience": {
                if (!item.company) item.company = "";
                if (!item.position) item.position = "";
                if (!item.location) item.location = "";
                if (!item.date) item.date = "";
                if (!item.summary) item.summary = "";
                item.url = fixUrl(item.url);
                break;
              }

              case "education": {
                if (!item.institution) item.institution = "";
                if (!item.area) item.area = "";
                if (!item.studyType) item.studyType = "";
                if (!item.date) item.date = "";
                if (!item.score) item.score = "";
                if (!item.summary) item.summary = "";
                item.url = fixUrl(item.url);
                break;
              }

              case "projects": {
                if (!item.name) item.name = "";
                if (!item.description) item.description = "";
                if (!item.date) item.date = "";
                if (!item.summary) item.summary = "";
                if (!item.keywords) item.keywords = [];
                item.url = fixUrl(item.url);
                break;
              }

              case "awards": {
                if (!item.title) item.title = "";
                if (!item.awarder) item.awarder = "";
                if (!item.date) item.date = "";
                if (!item.summary) item.summary = "";
                item.url = fixUrl(item.url);
                break;
              }

              case "certifications": {
                if (!item.name) item.name = "";
                if (!item.issuer) item.issuer = "";
                if (!item.date) item.date = "";
                if (!item.summary) item.summary = "";
                item.url = fixUrl(item.url);
                break;
              }

              case "volunteer": {
                if (!item.organization) item.organization = "";
                if (!item.position) item.position = "";
                if (!item.location) item.location = "";
                if (!item.date) item.date = "";
                if (!item.summary) item.summary = "";
                item.url = fixUrl(item.url);
                break;
              }

              case "skills": {
                if (!item.name) item.name = "";
                if (!item.description) item.description = "";
                if (item.level === undefined) item.level = 0;
                if (!item.keywords) item.keywords = [];
                break;
              }

              case "languages": {
                if (!item.name) item.name = "";
                if (!item.description) item.description = "";
                if (item.level === undefined) item.level = 0;
                break;
              }

              case "interests": {
                if (!item.name) item.name = "";
                if (!item.keywords) item.keywords = [];
                break;
              }

              case "profiles": {
                if (!item.network) item.network = "";
                if (!item.username) item.username = "";
                if (!item.icon) item.icon = "";
                item.url = fixUrl(item.url);
                break;
              }

              case "references": {
                if (!item.name) item.name = "";
                if (!item.description) item.description = "";
                if (!item.summary) item.summary = "";
                item.url = fixUrl(item.url);
                break;
              }

              case "signature": {
                if (!item.content) item.content = "";
                break;
              }

              case "hobbies": {
                if (!item.name) item.name = "";
                if (!item.keywords) item.keywords = [];
                break;
              }
            }
          }
        });
      }

      // Auto-visibility rules:
      // - If a section has non-empty items or non-empty content, force visible=true
      // - If a section has empty items array and no content, ensure visible=false
      try {
        const hasItems = Array.isArray(section?.items) && section.items.length > 0;
        const hasContent = typeof section?.content === "string" && section.content.trim() !== "";

        // Force visibility to true if section has content or items
        if (hasItems || hasContent) {
          section.visible = true;
          // logger.log(`🔍 Auto-visibility: Setting ${sectionKey} to visible=true (hasItems: ${hasItems}, hasContent: ${hasContent})`);
        } else {
          // Only set to false if explicitly empty
          section.visible = false;
          // logger.log(`🔍 Auto-visibility: Setting ${sectionKey} to visible=false (no content or items)`);
        }
      } catch (error) {
        logger.warn(`⚠️ Auto-visibility error for ${sectionKey}:`, error);
      }
    }
  }

  // Also fix the basics URL
  if (result.basics?.url) {
    result.basics.url = fixUrl(result.basics.url);
  }

  return result;
}

export class AiApiService {
  // Generate resume from prompt using CV styling agent with retry logic
  async generateResume(prompt: string, language?: string): Promise<ResumeGenerationResult> {
    try {
      console.log("🚀 generateResume called with language:", language);

      // Validate environment variables
      if (!AGENT_URL) {
        console.error("Missing AGENT_URL environment variable");
        throw new Error("Missing API URL configuration. Please check your environment variables.");
      }

      if (!AGENT_API_KEY) {
        console.error("Missing AGENT_API_KEY environment variable");
        throw new Error("Missing API key configuration. Please check your environment variables.");
      }

      // Validate prompt content
      if (!prompt || prompt.trim() === "") {
        throw new Error("Empty resume prompt provided");
      }

      // Log the full prompt for debugging
      logger.log("=== FULL RESUME GENERATION PROMPT START ===");
      logger.log(prompt);
      logger.log("=== FULL RESUME GENERATION PROMPT END ===");

      logger.log("Generating resume with prompt:", prompt.slice(0, 200) + "...");

      // Check if running in development/localhost and use CORS proxy if needed
      const isLocalhost =
        window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

      // Base API endpoint
      const apiEndpoint = `${AGENT_URL}/api/v1/chat/completions`;

      // For development testing only - use a CORS proxy for localhost
      if (isLocalhost) {
        logger.log("Running in localhost environment, attempting to use CORS proxy");

        // Option 1: Try the community CORS Anywhere proxy (may require activation)
        // apiEndpoint = `https://cors-anywhere.herokuapp.com/${AGENT_URL}/api/v1/chat/completions`;

        // Option 2: Use a more reliable proxy like allOrigins
        // apiEndpoint = `https://api.allorigins.win/raw?url=${encodeURIComponent(`${AGENT_URL}/api/v1/chat/completions`)}`;

        // Option 3: Directly use the API and rely on browser CORS extensions for testing
        logger.log("Using direct API call - ensure CORS is allowed in your browser");
      }

      // Retry logic with exponential backoff
      const maxRetries = 2;
      const baseTimeout = 300_000; // Start with 5 minutes (300 seconds) for AI operations
      let lastError: any = null;

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        const timeout = baseTimeout * (attempt + 1); // 90s, 180s, 270s

        logger.log(`Attempt ${attempt + 1}/${maxRetries + 1} - Calling AI agent at:`, apiEndpoint);
        logger.log(`Starting API call with ${timeout / 1000}-second timeout...`);
        logger.log("Time:", new Date().toISOString());

        // Create AbortController for timeout
        const controller = new AbortController();
        let isTimedOut = false;
        const timeoutId = setTimeout(() => {
          console.error(
            `⏱️ API call timed out after ${timeout / 1000} seconds (attempt ${attempt + 1})`,
          );
          console.error("Time:", new Date().toISOString());
          isTimedOut = true;
          controller.abort();
        }, timeout);

        try {
          const startTime = Date.now();
          logger.log("📡 Sending fetch request...");

          const requestBody = {
            messages: [
              {
                role: "system",
                content: PromptBuilder.SystemPromptStylingCV(),
              },
              {
                role: "user",
                content: prompt,
              },
            ],
            temperature: 0.3,
            top_p: 1,
            max_tokens: 8000,
            response_format: { type: "json_object" },
          };

          logger.log("Request body size:", JSON.stringify(requestBody).length, "characters");

          const response = await fetch(apiEndpoint, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${AGENT_API_KEY}`,
              "Content-Type": "application/json",
            },
            signal: controller.signal, // Add abort signal
            body: JSON.stringify(requestBody),
          });

          // Clear timeout if request completes
          clearTimeout(timeoutId);
          const responseTime = Date.now() - startTime;
          logger.log(`✅ API response received in ${responseTime}ms`);
          logger.log("Time:", new Date().toISOString());

          // Check for HTTP errors
          if (!response.ok) {
            const errorText = await response.text().catch(() => "Unknown error");
            console.error(`API error (${response.status}):`, errorText);
            throw new Error(`API error (${response.status}): ${errorText || response.statusText}`);
          }

          const responseText = await response.text();
          logger.log("Raw response text:", responseText.slice(0, 1000) + "...");

          let result;
          try {
            result = JSON.parse(responseText);
          } catch (jsonError) {
            console.error("Failed to parse API response as JSON:", jsonError);
            // If the response is not valid JSON, create a fallback object
            // that contains the raw text so we can still see the content
            result = {
              choices: [
                {
                  message: {
                    content: responseText,
                  },
                },
              ],
            };
          }

          // Extract actual token usage from the DigitalOcean API response
          const tokenUsage = result?.usage?.total_tokens;
          logger.log("Token usage from API:", tokenUsage);

          // Check if the response has the expected format
          const output = result?.choices?.[0]?.message?.content;
          if (!output || typeof output !== "string" || output.trim() === "") {
            console.error("Invalid or empty response from AI agent:", result);
            throw new Error("No content returned by AI agent. Please try again.");
          }

          // Log the full raw response for debugging
          logger.log("===FULL RAW RESPONSE===");
          logger.log(output);
          logger.log("===END FULL RAW RESPONSE===");

          // Post-process the output to enforce the required schema
          let safeResumeJson;
          try {
            safeResumeJson = enforceSchema(output, language);
            console.log(
              "✅ enforceSchema completed, final locale:",
              (safeResumeJson as any).metadata?.locale,
            );
          } catch (error) {
            console.error("Post-processing failed:", error);
            throw new Error("Failed to parse or enforce schema on AI output.");
          }

          return {
            content: JSON.stringify(safeResumeJson),
            tokenUsage: tokenUsage || null,
          };
        } catch (fetchError: any) {
          // Clear timeout if error occurs
          clearTimeout(timeoutId);

          console.error(`❌ Fetch error (attempt ${attempt + 1}):`, fetchError);
          console.error("Error name:", fetchError.name);
          console.error("Error message:", fetchError.message);
          console.error("Time:", new Date().toISOString());

          lastError = fetchError;

          // Check if it was aborted due to timeout
          if (fetchError.name === "AbortError" || isTimedOut) {
            console.error(`Request was aborted due to timeout (attempt ${attempt + 1})`);

            // If this was the last attempt, throw the error
            if (attempt === maxRetries) {
              throw new Error(
                `Request timed out after ${maxRetries + 1} attempts. The AI service is not responding. Please try again later.`,
              );
            } else {
              logger.log(`⚠️ Retrying with longer timeout... (attempt ${attempt + 2} coming up)`);
              // Wait a bit before retrying to give the service time to recover
              await new Promise((resolve) => setTimeout(resolve, 2000));
              continue; // Try next attempt
            }
          }

          // Check for network errors
          if (fetchError.message?.includes("Failed to fetch")) {
            console.error("Network error - cannot reach AI service");

            // Don't retry network errors
            throw new Error(
              "Cannot connect to AI service. Please check your internet connection and try again.",
            );
          }

          // For other errors, retry if we have attempts left
          if (attempt < maxRetries) {
            logger.log(`⚠️ Retrying due to error... (attempt ${attempt + 2} coming up)`);
            // Wait a bit before retrying
            await new Promise((resolve) => setTimeout(resolve, 2000));
            continue;
          }

          // Handle other fetch errors on final attempt
          throw fetchError; // Rethrow to be handled by the outer catch
        }
      } // End of retry loop

      // If we get here, all retries failed
      const error_ = lastError
        ? lastError
        : new Error("Failed to generate resume after multiple attempts.");
      throw error_;
    } catch (error: any) {
      console.error("Resume generation error:", error);

      // Provide more specific error messages based on error type
      if (error.message?.includes("timed out")) {
        toast({
          title: "⏱️ AI Service is Busy",
          description: i18n._(msg`The AI service is experiencing high load. Please try again in a moment.`),
          variant: "error",
        });
        throw new Error("The AI service is currently busy. Please wait a moment and try again.");
      } else if (error.message?.includes("multiple attempts")) {
        toast({
          title: "⏱️ Service Temporarily Unavailable",
          description: i18n._(msg`The AI service is not responding after multiple attempts. Please try again later.`),
          variant: "error",
        });
        throw error;
      } else if (error.message?.includes("Failed to fetch")) {
        // Use imported toast function directly
        toast({
          title: "❌ Connection error",
          description: i18n._(msg`Could not connect to the resume service. Please check your internet connection.`),
          variant: "error",
        });
        throw new Error(
          "Could not connect to resume service. Please check your internet connection.",
        );
      } else {
        toast({
          title: "❌ Resume generation failed",
          description: error.message || "An error occurred while calling the resume agent.",
          variant: "error",
        });
        throw error;
      }
    }
  }

  // Generate ATS analytics for a resume
  async generateATSAnalytics(
    resumeData: any,
    jobRole?: string,
    jobDescription?: string,
    marketContext?: any,
  ): Promise<ATSAnalyticsResult | null> {
    try {
      // Auto-extract job role from resume data if not provided
      let targetJobRole = jobRole;

      if (!targetJobRole) {
        // Try to get job role from resume basics.headline
        targetJobRole = resumeData?.basics?.headline || resumeData?.basics?.title;
      }

      // Validate that we have a job role for analysis
      if (!targetJobRole || targetJobRole.trim() === "") {
        throw new Error(
          "Job role is required for ATS analysis. Please add a headline/job title in your resume's basic information section.",
        );
      }

      logger.log("Generating ATS analytics for job role:", targetJobRole);

      // Use the agents service for unified access to MCP agents
      const result = await agentsService.atsScore(
        JSON.stringify(resumeData),
        jobDescription || "",
        marketContext,
      );

      if (!result) {
        console.error("Empty response from ATS analytics API");
        return null;
      }

      // Extract token usage if available (mocked for now as MCP doesn't return usage yet)
      const tokenUsage = 0;

      return {
        ...result,
        tokenUsage,
      };
    } catch (error) {
      console.error("Error generating ATS analytics:", error);
      return null;
    }
  }

  // Refine a specific section of the resume
  async refineResumeSection(
    sectionName: string,
    sectionData: any,
    suggestion: string,
    targetRole?: string,
  ): Promise<any | null> {
    try {
      logger.log(`Refining section: ${sectionName} with suggestion: ${suggestion}`);

      const result = await agentsService.refineSegment(
        sectionName,
        sectionData,
        suggestion,
        targetRole || "Software Engineer", // Fallback if no role found
      );

      if (!result) {
        console.error("Empty response from resume refinement API");
        return null;
      }

      return result;
    } catch (error) {
      console.error("Error refining resume section:", error);
      return null;
    }
  }

  // Get O*NET data with real API integration
  async getOnetData(jobTitle: string): Promise<{ duties: string; tools: string }> {
    try {
      if (!jobTitle || jobTitle.trim() === "") {
        return {
          duties: "- Please provide a job title to get relevant duties.",
          tools: "- Please provide a job title to get relevant tools.",
        };
      }

      if (process.env.NODE_ENV === "development") {
        logger.log("Fetching O*NET data for:", jobTitle);
      }

      // Check if O*NET API credentials are available
      if (!ONET_API_USERNAME || !ONET_API_PASSWORD) {
        logger.warn("O*NET API credentials not found, using fallback data");
        return this.getFallbackOnetData(jobTitle);
      }

      // Due to CORS issues with direct API calls in the browser, we'll use the fallback data
      // This could be replaced with a backend proxy solution in the future
      logger.warn("Using fallback data instead of O*NET API due to CORS restrictions");
      return this.getFallbackOnetData(jobTitle);

      /* The following code is commented out due to CORS issues
      // Step 1: Search for occupation code
      const searchUrl = `https://services.onetcenter.org/ws/online/search?keyword=${encodeURIComponent(jobTitle)}`;
      
      const headers = {
        'Authorization': 'Basic ' + btoa(`${ONET_API_USERNAME}:${ONET_API_PASSWORD}`),
        'Accept': 'application/json'
      };
      
      const searchResponse = await fetch(searchUrl, { headers });
      
      if (!searchResponse.ok) {
        console.error('O*NET search API error:', searchResponse.status);
        return this.getFallbackOnetData(jobTitle);
      }
      
      const searchData = await searchResponse.json();
      
      if (!searchData.occupation || searchData.occupation.length === 0) {
        logger.warn('No O*NET occupations found for:', jobTitle);
        return this.getFallbackOnetData(jobTitle);
      }
      
      // Get the most relevant occupation code
      const occupationCode = searchData.occupation[0].code;
      
      // Step 2: Get occupation details
      const detailsUrl = `https://services.onetcenter.org/ws/online/occupations/${occupationCode}`;
      const detailsResponse = await fetch(detailsUrl, { headers });
      
      if (!detailsResponse.ok) {
        console.error('O*NET details API error:', detailsResponse.status);
        return this.getFallbackOnetData(jobTitle);
      }
      
      const detailsData = await detailsResponse.json();
      
      // Step 3: Get tasks data
      const tasksUrl = `https://services.onetcenter.org/ws/online/occupations/${occupationCode}/details/tasks`;
      const tasksResponse = await fetch(tasksUrl, { headers });
      
      if (!tasksResponse.ok) {
        console.error('O*NET tasks API error:', tasksResponse.status);
        return this.getFallbackOnetData(jobTitle);
      }
      
      const tasksData = await tasksResponse.json();
      
      // Step 4: Get technology skills data
      const techUrl = `https://services.onetcenter.org/ws/online/occupations/${occupationCode}/details/technology_skills`;
      const techResponse = await fetch(techUrl, { headers });
      
      let techData = null;
      if (techResponse.ok) {
        techData = await techResponse.json();
      }
      
      // Process the data
      // Extract tasks
      let duties = '';
      if (tasksData && tasksData.task) {
        duties = tasksData.task
          .slice(0, 7) // Limit to 7 tasks
          .map((task: any) => `- ${task.statement}`)
          .join('\n');
      }
      
      // Extract technology skills and tools
      let tools = '';
      if (techData && techData.technology_skill) {
        tools = techData.technology_skill
          .slice(0, 7) // Limit to 7 technologies
          .map((tech: any) => `- ${tech.example}`)
          .join('\n');
      } else {
        // If no technology skills, try to extract knowledge areas as alternatives
        const knowledgeUrl = `https://services.onetcenter.org/ws/online/occupations/${occupationCode}/details/knowledge`;
        const knowledgeResponse = await fetch(knowledgeUrl, { headers });
        
        if (knowledgeResponse.ok) {
          const knowledgeData = await knowledgeResponse.json();
          tools = knowledgeData.knowledge
            .slice(0, 7)
            .map((knowledge: any) => `- ${knowledge.title}`)
            .join('\n');
        }
      }
      
      // If we still don't have duties or tools, use fallback
      if (!duties) {
        logger.warn('No duties found in O*NET data, using fallback');
        const fallback = this.getFallbackOnetData(jobTitle);
        duties = fallback.duties;
      }
      
      if (!tools) {
        logger.warn('No tools found in O*NET data, using fallback');
        const fallback = this.getFallbackOnetData(jobTitle);
        tools = fallback.tools;
      }
      
      return { duties, tools };
      */
    } catch (error: any) {
      console.error("O*NET fetch error:", error);

      // Display a more user-friendly message for CORS errors
      if (
        error.message &&
        (error.message.includes("CORS") ||
          error.message.includes("blocked by") ||
          error.message.includes("network error"))
      ) {
        logger.warn("CORS issue detected while fetching O*NET data, using fallback data");
        toast({
          title: "O*NET API Access Issue",
          description: i18n._(msg`Using built-in job data due to API access restrictions.`),
          variant: "default",
        });
      }

      return this.getFallbackOnetData(jobTitle);
    }
  }

  // Enhanced fallback data for when O*NET API fails
  private getFallbackOnetData(jobTitle: string): { duties: string; tools: string } {
    if (process.env.NODE_ENV === "development") {
      logger.log("Using fallback O*NET data for:", jobTitle);
    }

    const jobTitleLower = jobTitle.toLowerCase();

    // Create a mock response based on common job titles
    const mockData: Record<string, { duties: string; tools: string }> = {
      "data scientist": {
        duties:
          "- Identify valuable data sources and automate collection processes\n- Undertake preprocessing of structured and unstructured data\n- Analyze large amounts of information to discover trends and patterns\n- Build predictive models and machine-learning algorithms\n- Combine models through ensemble modeling\n- Present information using data visualization techniques",
        tools:
          "- Python\n- R\n- SQL\n- Tableau\n- PowerBI\n- TensorFlow\n- PyTorch\n- Scikit-learn\n- Jupyter Notebooks\n- Git",
      },
      "software engineer": {
        duties:
          "- Design and develop software systems\n- Test and evaluate new programs\n- Optimize software for speed and scalability\n- Write and test code\n- Consult with clients, engineers, security specialists, and other stakeholders\n- Collaborate with other developers",
        tools:
          "- JavaScript/TypeScript\n- Python\n- Java\n- Git\n- Docker\n- Kubernetes\n- VS Code\n- GitHub\n- JIRA\n- CI/CD pipelines",
      },
      "ux designer": {
        duties:
          "- Gather and evaluate user requirements in collaboration with product managers\n- Illustrate design ideas using storyboards, process flows, and sitemaps\n- Design graphic user interface elements\n- Build page navigation buttons and search fields\n- Develop UI mockups and prototypes\n- Create original graphic designs",
        tools:
          "- Figma\n- Adobe XD\n- Sketch\n- InVision\n- Axure\n- Balsamiq\n- UserTesting\n- Adobe Creative Suite\n- Miro\n- Zeplin",
      },
      "product manager": {
        duties:
          "- Define the product vision, strategy, and roadmap\n- Gather and prioritize product and customer requirements\n- Define product features and capabilities\n- Work closely with engineering teams\n- Ensure product meets business objectives\n- Analyze market trends and competition",
        tools:
          "- JIRA\n- Confluence\n- Asana\n- Trello\n- Slack\n- Google Analytics\n- Amplitude\n- Mixpanel\n- Airtable\n- Notion",
      },
      "marketing manager": {
        duties:
          "- Develop marketing strategies and plans\n- Manage marketing budget and resources\n- Oversee marketing campaigns from concept to execution\n- Analyze campaign performance metrics\n- Conduct market research\n- Manage relationships with agencies and vendors",
        tools:
          "- Google Analytics\n- HubSpot\n- Marketo\n- Mailchimp\n- SEMrush\n- Adobe Creative Suite\n- Canva\n- Hootsuite\n- Salesforce\n- Ahrefs",
      },
      "frontend developer": {
        duties:
          "- Create responsive user interface components\n- Implement designs with HTML, CSS, and JavaScript\n- Optimize applications for maximum speed and scalability\n- Collaborate with back-end developers and web designers\n- Debug application code and troubleshoot interface issues\n- Stay up-to-date with emerging front-end technologies",
        tools:
          "- JavaScript/TypeScript\n- React/Angular/Vue\n- HTML5/CSS3\n- Sass/Less\n- Webpack\n- Jest/Testing Library\n- Git\n- Responsive design principles\n- Browser developer tools\n- Performance optimization techniques",
      },
      "backend developer": {
        duties:
          "- Design and implement server-side logic and databases\n- Build efficient APIs and services\n- Optimize application performance and scalability\n- Ensure data security and protection\n- Implement authentication and authorization\n- Collaborate with frontend developers on integration",
        tools:
          "- Node.js/Python/Java/Go\n- SQL and NoSQL databases\n- RESTful API design\n- GraphQL\n- Docker/Kubernetes\n- AWS/Azure/GCP\n- Microservices architecture\n- Message queues\n- Git\n- CI/CD pipelines",
      },
      "graphic designer": {
        duties:
          "- Create visual concepts to communicate ideas\n- Develop graphics for product illustrations, logos, and websites\n- Design layouts for marketing materials and digital content\n- Select colors, images, typography, and layout\n- Incorporate feedback from clients and stakeholders\n- Ensure final graphics and layouts are visually appealing and on-brand",
        tools:
          "- Adobe Photoshop\n- Adobe Illustrator\n- Adobe InDesign\n- Figma\n- Sketch\n- Typography tools\n- Color theory applications\n- Digital asset management systems\n- Branding guidelines software\n- Collaboration tools",
      },
      "data analyst": {
        duties:
          "- Collect and interpret data from various sources\n- Clean and preprocess data for analysis\n- Identify patterns and trends in complex datasets\n- Create data visualizations and dashboard reports\n- Develop and maintain databases/data systems\n- Present findings to stakeholders in an understandable manner",
        tools:
          "- SQL\n- Excel/Google Sheets\n- Python/R\n- Tableau/Power BI\n- Google Analytics\n- Statistical analysis tools\n- ETL tools\n- Data cleaning libraries\n- Data visualization frameworks\n- Business intelligence platforms",
      },
      "project manager": {
        duties:
          "- Define project scope, goals, and deliverables\n- Plan and schedule project timelines\n- Track deliverables and ensure deadlines are met\n- Allocate resources and manage budget\n- Identify and mitigate risks and blockers\n- Communicate with stakeholders on progress and changes",
        tools:
          "- JIRA/Asana/Monday.com\n- Microsoft Project\n- Gantt charts\n- Agile methodologies\n- Scrum frameworks\n- Risk management tools\n- Resource planning software\n- Slack/Microsoft Teams\n- Documentation tools\n- Reporting dashboards",
      },
      "content writer": {
        duties:
          "- Research industry-related topics\n- Write clear, compelling copy for various channels\n- Edit and proofread written materials\n- Develop content strategy with marketing team\n- Follow SEO best practices\n- Adapt tone and style based on brand voice and audience",
        tools:
          "- Content management systems\n- SEO tools (SEMrush, Ahrefs)\n- Grammar checkers (Grammarly)\n- Google Analytics\n- Keyword research tools\n- Plagiarism checkers\n- Editorial calendars\n- Style guides\n- Collaboration platforms\n- Project management tools",
      },
      seo: {
        duties:
          "- Develop and implement SEO strategies\n- Conduct keyword research\n- Monitor website performance and traffic\n- Analyze and report on SEO performance\n- Optimize website content\n- Perform competitors research",
        tools:
          "- SEMrush\n- Ahrefs\n- Moz\n- Google Analytics\n- Google Search Console\n- Screaming Frog\n- Yoast SEO\n- Surfer SEO\n- Clearscope\n- PageSpeed Insights",
      },
    };

    let matchedData: { duties: string; tools: string } | null = null;

    // Try to find an exact match first
    if (mockData[jobTitleLower]) {
      matchedData = mockData[jobTitleLower];
    } else {
      // Try to find a partial match
      for (const [key, value] of Object.entries(mockData)) {
        if (jobTitleLower.includes(key) || key.includes(jobTitleLower)) {
          matchedData = value;
          break;
        }
      }
    }

    // If we found a match, use it; otherwise, provide generic data
    if (matchedData) {
      if (process.env.NODE_ENV === "development") {
        logger.log(`Using mock O*NET data for ${jobTitle}`);
      }
      return matchedData;
    }

    // If no match found, provide generic professional data
    return {
      duties: `- No specific O*NET duties found for "${jobTitle}". Using common professional duties:\n- Research industry trends and best practices\n- Collaborate with cross-functional teams\n- Develop and implement strategies\n- Analyze data and present findings\n- Manage projects and prioritize tasks`,
      tools: `- No specific O*NET tools found for "${jobTitle}". Using common professional tools:\n- Microsoft Office Suite\n- G Suite\n- Project Management Software\n- Data Analysis Tools\n- Communication Platforms`,
    };
  }

  // Get market trends from Perplexity API with rate limiting and expanded queries
  async getLiveTrends(jobTitle: string, country: string): Promise<string> {
    try {
      if (!jobTitle || jobTitle.trim() === "") {
        return "- Please provide a job title to get market trends.";
      }

      if (process.env.NODE_ENV === "development") {
        logger.log("📡 Fetching Perplexity data for:", jobTitle, "in", country);
      }

      if (!import.meta.env.VITE_PERPLEXITY_API_KEY) {
        logger.warn("⚠️ Perplexity API key not configured. Using fallback market trends.");
        return this.getFallbackMarketTrends(jobTitle, country);
      }

      // Check cache first - 1 hour cache
      const cacheKey = `trends-${jobTitle}-${country}`.toLowerCase().replace(/\s+/g, "-");
      const cached = localStorage.getItem(cacheKey);
      const cacheTime = localStorage.getItem(`${cacheKey}-time`);

      if (cached && cacheTime && Date.now() - Number.parseInt(cacheTime) < 3_600_000) {
        logger.log("📦 Using cached market trends data");
        return cached;
      }

      // Use the consolidated prompt builder for market trends prompt
      const prompt = PromptBuilder.getMarketTrendsPrompt(jobTitle, country);

      // Use rate limiter to prevent 429 errors with short timeout
      const rateLimiter = PerplexityRateLimiter.getInstance();

      const requestFn = async (abortSignal?: AbortSignal) => {
        // Use the provided abort signal, or create our own with a longer timeout
        const controller = abortSignal ? undefined : new AbortController();
        const signal = abortSignal || controller?.signal;
        const timeoutId = abortSignal ? undefined : setTimeout(() => controller?.abort(), 45_000);

        try {
          // Updated request body with current Perplexity API format
          const requestBody = {
            model: "sonar", // Current valid model name
            messages: [
              {
                role: "system",
                content:
                  "You are a professional career market researcher. Provide accurate, current job market information in a structured format.",
              },
              {
                role: "user",
                content: prompt,
              },
            ],
            temperature: 0.3,
            max_tokens: 1500,
          };

          if (process.env.NODE_ENV === "development") {
            logger.log("Perplexity API request body:", JSON.stringify(requestBody, null, 2));
          }

          const response = await fetch("https://api.perplexity.ai/chat/completions", {
            method: "POST",
            headers: {
              Authorization: `Bearer ${import.meta.env.VITE_PERPLEXITY_API_KEY}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: signal,
          });

          if (timeoutId) clearTimeout(timeoutId);
          return response;
        } finally {
          if (timeoutId) clearTimeout(timeoutId);
        }
      };

      try {
        const content = await rateLimiter.makeRequest(
          `live-trends-${jobTitle}-${country}`,
          async (abortSignal) => {
            // Use the abort signal from rate limiter if provided
            const response = await requestFn(abortSignal);

            if (process.env.NODE_ENV === "development") {
              logger.log("Perplexity API response status:", response.status, response.statusText);
            }

            if (!response.ok) {
              const errorText = await response.text().catch(() => i18n._(msg`Unknown error`));
              console.error(`Perplexity API error (${response.status}):`, errorText);
              const error = new Error(`HTTP ${response.status}: ${errorText}`) as Error & {
                status: number;
              };
              error.status = response.status;
              throw error;
            }

            const responseData = await response.json();

            if (process.env.NODE_ENV === "development") {
              logger.log("Perplexity API full response:", responseData);
            }

            const content = responseData?.choices?.[0]?.message?.content || "";

            if (process.env.NODE_ENV === "development") {
              logger.log("Perplexity API content:", content);
            }

            if (content && content.trim() !== "") {
              return content;
            } else {
              logger.warn("Empty content received from Perplexity API");
              return "";
            }
          },
          cacheKey,
          30 * 60 * 1000, // 30 minute cache
          45_000, // 45 second timeout
        );

        // Cache successful result
        if (content?.trim()) {
          localStorage.setItem(cacheKey, content);
          localStorage.setItem(`${cacheKey}-time`, Date.now().toString());
          logger.log("💾 Cached market trends data for 1 hour");
        }

        // Return the processed data
        return content || this.getFallbackMarketTrends(jobTitle, country);
      } catch (error) {
        console.error("🚨 Perplexity API timeout/error:", error);
        logger.log("🔄 Using fallback market trends data");
        return this.getFallbackMarketTrends(jobTitle, country);
      }
    } catch (error) {
      console.error("🚨 Perplexity general error:", error);
      logger.log("🔄 Using fallback market trends data");
      return this.getFallbackMarketTrends(jobTitle, country);
    }
  }

  // Test Perplexity API connection (moved from perplexity-debug.ts)
  async testPerplexityAPI(): Promise<{
    success: boolean;
    data?: string;
    error?: string;
  }> {
    console.log("Testing Perplexity API with rate limiting...");
    console.log("API Key:", import.meta.env.VITE_PERPLEXITY_API_KEY ? "Present" : "Missing");

    if (!import.meta.env.VITE_PERPLEXITY_API_KEY) {
      console.warn("⚠️ Perplexity API key not configured. Using fallback data for market trends.");
      return { success: false, error: "API key not configured (using fallback data)" };
    }

    try {
      const rateLimiter = PerplexityRateLimiter.getInstance();

      const requestFn = async () => {
        const response = await fetch("https://api.perplexity.ai/chat/completions", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_PERPLEXITY_API_KEY}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: "sonar",
            messages: [
              {
                role: "user",
                content: "What are the current job market trends for software engineers?",
              },
            ],
            temperature: 0.3,
            max_tokens: 500,
          }),
        });

        console.log("Response status:", response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Error response:", errorText);
          const error = new Error(`HTTP ${response.status}: ${errorText}`) as Error & {
            status: number;
          };
          error.status = response.status;
          throw error;
        }

        return await response.json();
      };

      const data = await rateLimiter.makeRequest(
        "test-perplexity-api",
        requestFn,
        "test-api-connection",
        5 * 60 * 1000, // 5 minute cache
        15_000, // 15 second timeout
      );

      console.log("Success response:", data);

      return {
        success: true,
        data: data?.choices?.[0]?.message?.content || "No content returned",
      };
    } catch (error) {
      console.error("Fetch error:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : i18n._(msg`Unknown error`),
      };
    }
  }

  // Fallback market trends data when Perplexity API fails
  private getFallbackMarketTrends(jobTitle: string, country: string): string {
    logger.log(`🔄 Generating fallback market trends for ${jobTitle} in ${country}`);

    return `Market Intelligence for ${jobTitle} positions in ${country}:

📈 **Current Market Trends:**
- High demand with ${Math.floor(Math.random() * 15) + 10}% job growth expected
- Remote and hybrid opportunities increasing (${Math.floor(Math.random() * 30) + 60}% of positions)
- Competitive salary ranges with strong benefits packages
- Focus on emerging technologies and digital transformation

🎯 **In-Demand Skills:**
- Leadership and strategic thinking
- Problem-solving and analytical abilities  
- Technical proficiency and adaptability
- Communication and collaboration skills
- Industry-specific expertise and certifications

💼 **Industry Outlook:**
- Stable growth trajectory for ${new Date().getFullYear()}
- Investment in digital infrastructure and innovation
- Emphasis on sustainability and efficiency
- Cross-functional collaboration becoming essential

⚡ **Recommendations:**
- Highlight emerging technology experience
- Emphasize adaptability and continuous learning
- Showcase measurable achievements and impact
- Include relevant certifications and training

*Note: This is fallback data. Live market intelligence temporarily unavailable.*`;
  }

  // Fallback ATS analytics data
  // Fallback function removed - no more fake data

  // Helper method for aggressive JSON cleanup specifically for AI responses
  static aggressiveJSONCleanup(jsonString: string): string {
    let cleaned = jsonString;

    // Fix malformed patterns common in AI responses
    // Pattern: ": "propertyName": value -> "propertyName": value
    cleaned = cleaned.replace(/([,{]\s*)":\s*"([^"]+)"\s*:\s*/g, '$1"$2": ');

    // Pattern: ": "name\\": \\": value -> "name": "value
    cleaned = cleaned.replace(/([,{]\s*)":\s*"([^"]+)(?:\\+":\s*){2}([^\\]+)\\+"/g, '$1"$2": "$3"');

    // Remove excessive escaping
    cleaned = cleaned.replace(/\\+"/g, '"');
    cleaned = cleaned.replace(/\\+'/g, "'");

    // Fix trailing commas before closing braces/brackets
    cleaned = cleaned.replace(/,(\s*[\]}])/g, "$1");

    // Fix missing commas between objects
    cleaned = cleaned.replace(/}(\s*)"/g, '},$1"');
    cleaned = cleaned.replace(/](\s*)"/g, '],$1"');

    // Fix malformed property names (remove leading colons)
    cleaned = cleaned.replace(/([,{]\s*)":\s*([^"]+)"/g, '$1"$2"');

    // Fix double colons
    cleaned = cleaned.replace(/::\s*/g, ": ");

    return cleaned;
  }

  // Helper method to extract partial JSON data using regex patterns
  static extractPartialJSON(content: string): any {
    const extracted: any = {};

    try {
      // Extract name
      const nameMatch = /["'](?:name|fullname)["']\s*:\s*["']([^"']+)["']/i.exec(content);
      if (nameMatch) extracted.name = nameMatch[1];

      // Extract title/position
      const titleMatch = /["'](?:title|position|jobtitle|label)["']\s*:\s*["']([^"']+)["']/i.exec(
        content,
      );
      if (titleMatch) extracted.title = titleMatch[1];

      // Extract email
      const emailMatch = /["'](?:email|emailaddress)["']\s*:\s*["']([^"']+)["']/i.exec(content);
      if (emailMatch) extracted.email = emailMatch[1];

      // Extract phone
      const phoneMatch = /["'](?:phone|phonenumber|mobile)["']\s*:\s*["']([^"']+)["']/i.exec(
        content,
      );
      if (phoneMatch) extracted.phone = phoneMatch[1];

      // Extract location
      const locationMatch = /["'](?:location|address|city)["']\s*:\s*["']([^"']+)["']/i.exec(
        content,
      );
      if (locationMatch) extracted.location = locationMatch[1];

      // Extract professional summary
      const summaryMatch =
        /["'](?:professionalsummary|summary|about)["']\s*:\s*["']([^"']+)["']/i.exec(content);
      if (summaryMatch) extracted.professionalSummary = summaryMatch[1];

      // Extract skills array
      const skillsMatch = /["']skills["']\s*:\s*\[([^\]]+)]/i.exec(content);
      if (skillsMatch) {
        try {
          const skillsArray = skillsMatch[1]
            .split(",")
            .map((skill) => skill.trim().replace(/["']/g, ""));
          extracted.skills = skillsArray;
        } catch {
          logger.warn("Could not parse skills array");
        }
      }
    } catch (error) {
      console.error("Error in extractPartialJSON:", error);
    }

    return extracted;
  }
}

// Helper functions for fallback ATS analytics

function extractSkills(resumeData: any): string[] {
  const skills: string[] = [];

  // Extract from skills array
  if (resumeData?.skills && Array.isArray(resumeData.skills)) {
    resumeData.skills.forEach((skill: any) => {
      if (typeof skill === "string") {
        skills.push(skill);
      } else if (skill?.name) {
        skills.push(skill.name);
      }

      // Extract keywords if available
      if (skill?.keywords && Array.isArray(skill.keywords)) {
        skills.push(...skill.keywords);
      }
    });
  }

  // Extract from technicalSkills and softSkills arrays
  if (resumeData?.technicalSkills && Array.isArray(resumeData.technicalSkills)) {
    skills.push(...resumeData.technicalSkills);
  }

  if (resumeData?.softSkills && Array.isArray(resumeData.softSkills)) {
    skills.push(...resumeData.softSkills);
  }

  return [...new Set(skills)]; // Remove duplicates
}

function estimateExperienceYears(resumeData: any): number {
  let totalMonths = 0;

  // Check work experience
  const workExperience = resumeData?.work || resumeData?.professionalExperience || [];

  if (Array.isArray(workExperience) && workExperience.length > 0) {
    workExperience.forEach((job: any) => {
      const startDate = job.startDate || job.start_date;
      const endDate = job.endDate || job.end_date || "Present";

      if (startDate) {
        const start = new Date(startDate);
        let end;

        end = endDate === "Present" ? new Date() : new Date(endDate);

        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
          const months =
            (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
          totalMonths += months > 0 ? months : 0;
        }
      }
    });
  }

  return Math.round(totalMonths / 12);
}

function getRelevantKeywords(jobRole: string): string[] {
  const jobRoleLower = jobRole.toLowerCase();

  // Define keyword sets for different job categories
  const keywordSets: Record<string, string[]> = {
    developer: [
      "JavaScript",
      "TypeScript",
      "React",
      "Node.js",
      "API",
      "Frontend",
      "Backend",
      "Full Stack",
      "Git",
      "CI/CD",
      "Docker",
      "AWS",
      "Cloud",
      "Agile",
      "Scrum",
    ],
    designer: [
      "UI/UX",
      "User Experience",
      "User Interface",
      "Figma",
      "Adobe XD",
      "Sketch",
      "Wireframing",
      "Prototyping",
      "User Research",
      "Visual Design",
      "Interaction Design",
    ],
    manager: [
      "Leadership",
      "Strategy",
      "Team Management",
      "Project Management",
      "Stakeholder",
      "Budget",
      "KPIs",
      "Agile",
      "Scrum",
      "Resource Allocation",
      "Process Improvement",
    ],
    marketing: [
      "Digital Marketing",
      "SEO",
      "SEM",
      "Content Strategy",
      "Social Media",
      "Analytics",
      "Campaign Management",
      "Brand Development",
      "Market Research",
    ],
    data: [
      "Python",
      "R",
      "SQL",
      "Data Analysis",
      "Data Visualization",
      "Machine Learning",
      "Statistics",
      "Big Data",
      "Tableau",
      "Power BI",
      "ETL",
      "Data Modeling",
    ],
  };

  // Determine which category the job role falls into
  let category = "general";

  if (
    jobRoleLower.includes("develop") ||
    jobRoleLower.includes("engineer") ||
    jobRoleLower.includes("program") ||
    jobRoleLower.includes("code")
  ) {
    category = "developer";
  } else if (
    jobRoleLower.includes("design") ||
    jobRoleLower.includes("ux") ||
    jobRoleLower.includes("ui")
  ) {
    category = "designer";
  } else if (
    jobRoleLower.includes("manag") ||
    jobRoleLower.includes("direct") ||
    jobRoleLower.includes("lead")
  ) {
    category = "manager";
  } else if (
    jobRoleLower.includes("market") ||
    jobRoleLower.includes("brand") ||
    jobRoleLower.includes("content")
  ) {
    category = "marketing";
  } else if (
    jobRoleLower.includes("data") ||
    jobRoleLower.includes("analy") ||
    jobRoleLower.includes("scien")
  ) {
    category = "data";
  }

  // Return the relevant keywords
  return (
    keywordSets[category] || [
      "Communication",
      "Teamwork",
      "Problem Solving",
      "Adaptability",
      "Time Management",
      "Microsoft Office",
      "Project Management",
    ]
  );
}

function isProjectRelevant(jobRole: string): boolean {
  const jobRoleLower = jobRole.toLowerCase();

  // Projects are particularly relevant for these roles
  return (
    jobRoleLower.includes("develop") ||
    jobRoleLower.includes("engineer") ||
    jobRoleLower.includes("design") ||
    jobRoleLower.includes("ux") ||
    jobRoleLower.includes("ui") ||
    jobRoleLower.includes("data") ||
    jobRoleLower.includes("research")
  );
}

// Create a singleton instance without using React hooks
export const aiApiService = new AiApiService();
